import React, { useEffect, useState } from "react";
import { Search, PackageCheck, ArrowRightLeft, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const num = (n) => (n || 0).toLocaleString("id-ID");

export default function PreOrderReady() {
  const { can } = useAuth();
  const write = can("warehouse_admin", "sales");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [route, setRoute] = useState(null);

  const load = () => api.get("/preorder-batches/ready-stock", { params: { q: q || undefined } })
    .then((r) => setRows(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  return (
    <div data-testid="preorder-ready-page">
      <PageHeader title="Stok PO Siap Jual" subtitle="Kelebihan kedatangan Pre-Order — alihkan ke stok reguler atau batch lain" />
      <div className="relative flex-1 max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / batch..." className="pl-9 bg-white" data-testid="por-search" />
      </div>
      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Batch</TableHead><TableHead>Produk</TableHead><TableHead>Varian</TableHead>
              <TableHead className="text-right">Siap Jual</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`por-row-${r.id}`}>
                <TableCell className="text-sm"><span className="font-mono text-cocoa">{r.batch_code}</span><div className="text-xs text-wmuted">{r.batch_name}</div></TableCell>
                <TableCell className="font-medium text-cocoa">{r.product_name}{r.keep_as_po && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-cocoa/10 text-cocoa font-semibold align-middle" data-testid={`por-tetappo-${r.id}`}>Tetap PO</span>}</TableCell>
                <TableCell className="text-sm">{r.color}/{r.size}</TableCell>
                <TableCell className="text-right font-bold text-status-passed" data-testid={`por-ready-${r.id}`}>{num(r.ready_qty)}</TableCell>
                <TableCell className="text-right">
                  {write && <Button size="sm" onClick={() => setRoute(r)} className="bg-cocoa hover:bg-cocoa-dark text-ivory h-8" data-testid={`por-route-${r.id}`}><ArrowRightLeft className="w-4 h-4 mr-1.5" />Alihkan</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-10"><PackageCheck className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada stok PO siap jual.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>
      {route && <RouteDialog line={route} onClose={() => setRoute(null)} onDone={() => { setRoute(null); load(); }} />}
    </div>
  );
}

function RouteDialog({ line, onClose, onDone }) {
  const [action, setAction] = useState(line.keep_as_po ? "keep" : "regular");
  const [qty, setQty] = useState(line.ready_qty);
  const [batches, setBatches] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/preorder-batches", { params: { status: "Open" } }).then((r) => setBatches((r.data.items || []).filter((b) => b.id !== line.batch_id))).catch(() => {}); }, [line.batch_id]);

  const submit = async () => {
    const n = parseInt(qty) || 0;
    if (n <= 0 || n > line.ready_qty) return toast.error(`Qty 1–${line.ready_qty}`);
    if (action === "transfer" && !target) return toast.error("Pilih batch tujuan");
    setBusy(true);
    try {
      await api.post(`/preorder-batches/lines/${line.id}/route`, { action, qty: n, target_batch_id: action === "transfer" ? target : undefined });
      toast.success("Stok PO dialihkan"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="por-route-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Alihkan Stok PO</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-wmuted">{line.product_name} {line.color}/{line.size} · siap jual <b className="text-status-passed">{num(line.ready_qty)}</b></div>
          <div><Label>Tujuan</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="bg-white" data-testid="por-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="keep">Biarkan sebagai PO siap jual</SelectItem>
                {!line.keep_as_po && <SelectItem value="regular">Pindah ke stok reguler (gudang HOME)</SelectItem>}
                <SelectItem value="transfer">Oper ke batch lain</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {action === "transfer" && (
            <div><Label>Batch Tujuan</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="bg-white" data-testid="por-target"><SelectValue placeholder="Pilih batch" /></SelectTrigger>
                <SelectContent>{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {action !== "keep" && <div><Label>Qty</Label><Input type="number" min={1} max={line.ready_qty} value={qty} onChange={(e) => setQty(e.target.value)} className="bg-white" data-testid="por-qty" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="por-submit"><Warehouse className="w-4 h-4 mr-1.5" />{busy ? "Memproses..." : "Alihkan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
